import { useCallback, useEffect, useRef, useState } from 'react'

const findBlockIdFromComposedPath = (path: EventTarget[]): number | null => {
  for (const target of path) {
    if (!(target instanceof HTMLElement)) continue
    const raw = target.getAttribute('data-block-id')
    if (raw !== null) return Number(raw)
  }
  return null
}

const syncBlockAttribute = (
  shadowRoot: ShadowRoot,
  attribute: string,
  predicate: (blockId: number) => boolean
): void => {
  shadowRoot.querySelectorAll<HTMLElement>('[data-block-id]').forEach((el) => {
    const blockId = Number(el.getAttribute('data-block-id'))
    if (predicate(blockId)) el.setAttribute(attribute, 'true')
    else el.removeAttribute(attribute)
  })
}

export interface UseContentBriefShadowInteractionProps {
  hostRef: React.RefObject<HTMLDivElement | null>
  commentMap: Record<number, string>
}

export interface UseContentBriefShadowInteractionReturn {
  activeBlockIndex: number | null
  activeBlockRect: DOMRect | null
  closeActiveBlock: () => void
}

export const useContentBriefShadowInteraction = ({
  hostRef,
  commentMap,
}: UseContentBriefShadowInteractionProps): UseContentBriefShadowInteractionReturn => {
  const [activeBlockIndex, setActiveBlockIndex] = useState<number | null>(null)
  const [activeBlockRect, setActiveBlockRect] = useState<DOMRect | null>(null)
  const lastActiveElement = useRef<HTMLElement | null>(null)

  const closeActiveBlock = useCallback((): void => {
    setActiveBlockIndex(null)
    setActiveBlockRect(null)
    lastActiveElement.current = null
  }, [])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined

    const handleClick = (event: MouseEvent): void => {
      const blockId = findBlockIdFromComposedPath(event.composedPath())
      if (blockId === null) return
      const shadow = host.shadowRoot
      if (!shadow) return
      const target = shadow.querySelector<HTMLElement>(`[data-block-id="${blockId}"]`)
      if (!target) return
      lastActiveElement.current = target
      setActiveBlockIndex(blockId)
      setActiveBlockRect(target.getBoundingClientRect())
    }

    host.addEventListener('click', handleClick)
    return () => host.removeEventListener('click', handleClick)
  }, [hostRef])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined
    const handleScroll = (): void => closeActiveBlock()
    host.addEventListener('scroll', handleScroll, { passive: true })
    return () => host.removeEventListener('scroll', handleScroll)
  }, [hostRef, closeActiveBlock])

  useEffect(() => {
    const host = hostRef.current
    const shadow = host?.shadowRoot
    if (!shadow) return
    syncBlockAttribute(shadow, 'data-has-comment', (id) => Boolean(commentMap[id]))
  }, [hostRef, commentMap])

  useEffect(() => {
    const host = hostRef.current
    const shadow = host?.shadowRoot
    if (!shadow) return
    syncBlockAttribute(shadow, 'data-active', (id) => id === activeBlockIndex)
  }, [hostRef, activeBlockIndex])

  return { activeBlockIndex, activeBlockRect, closeActiveBlock }
}
